import { useEffect, useMemo, useRef, useState } from "react";
import { CheckOutlined, SearchOutlined } from "@ant-design/icons";
import { Modal } from "antd";

import styles from "./QuickSearchModal.module.scss";

interface FacilitySwitcherModalProps {
  clinics: IClinic[];
  onClose: () => void;
  onSelect: (clinicId: number) => void;
  open: boolean;
  selectedClinicId?: number;
}

const FacilitySwitcherModal = ({
  clinics,
  onClose,
  onSelect,
  open,
  selectedClinicId,
}: FacilitySwitcherModalProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const filteredClinics = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return clinics;
    return clinics.filter((clinic) =>
      clinic.name.toLowerCase().includes(normalizedQuery)
    );
  }, [clinics, query]);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={560}
      style={{ top: 88 }}
      styles={{ body: { padding: 0 } }}
      className={styles.modal}
      destroyOnHidden
      afterOpenChange={(isOpen) => {
        if (isOpen) inputRef.current?.focus();
      }}
    >
      <div className={styles.searchBar}>
        <SearchOutlined className={styles.searchBarIcon} />
        <input
          ref={inputRef}
          className={styles.searchBarInput}
          placeholder="Search facilities by name…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          aria-label="Search facilities"
        />
      </div>
      <div className={styles.results}>
        {filteredClinics.length === 0 ? (
          <div className={styles.hint}>
            {clinics.length === 0
              ? "No facilities available."
              : `No matches for “${query.trim()}”.`}
          </div>
        ) : (
          filteredClinics.map((clinic) => (
            <button
              key={clinic.id}
              type="button"
              className={styles.facilityResultRow}
              onClick={() => {
                onSelect(clinic.id);
                onClose();
              }}
            >
              <span className={styles.resultName}>{clinic.name}</span>
              {clinic.id === selectedClinicId && (
                <CheckOutlined className={styles.selectedIcon} />
              )}
            </button>
          ))
        )}
      </div>
    </Modal>
  );
};

export default FacilitySwitcherModal;
